import React, { useEffect, useRef } from "react";
import { useAirQualityGeoJSON, useFireHotspots } from "../hooks/useData";

const AQI_COLORS = {
  good: "#10B981", moderate: "#FBBF24",
  unhealthy_sensitive: "#F97316", unhealthy: "#F97316",
  very_unhealthy: "#EF4444", hazardous: "#FF4444",
};

export default function GISMap() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const { data: aqGeoJSON } = useAirQualityGeoJSON();
  const { data: fires } = useFireHotspots();

  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    // Dynamically load Leaflet CSS
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    // Dynamically load Leaflet JS
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => {
      const L = window.L;
      const map = L.map(mapRef.current, {
        center: [20, 0],
        zoom: 2,
        zoomControl: true,
        attributionControl: false,
      });

      L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
        maxZoom: 18,
      }).addTo(map);

      mapInstanceRef.current = map;
      mapInstanceRef.current._L = L;

      // Trigger layer rendering if data already loaded
      window.dispatchEvent(new Event("mapReady"));
    };
    document.body.appendChild(script);
  }, []);

  // AQ overlay
  useEffect(() => {
    const render = () => {
      const map = mapInstanceRef.current;
      const L = map?._L;
      if (!map || !L || !aqGeoJSON?.features) return;
      if (map._aqLayer) map.removeLayer(map._aqLayer);
      map._aqLayer = L.geoJSON(aqGeoJSON, {
        pointToLayer: (feature, latlng) => {
          const cat = feature.properties?.aqi_category || "unknown";
          return L.circleMarker(latlng, {
            radius: 6,
            fillColor: AQI_COLORS[cat] || "#7A8EA8",
            color: "transparent",
            fillOpacity: 0.8,
          });
        },
        onEachFeature: (feature, layer) => {
          const p = feature.properties;
          layer.bindPopup(`
            <div style="font-family:monospace;font-size:11px;background:#0D1421;color:#E8EDF5;padding:8px;border-radius:6px">
              <b>${p.location || p.city || "Unknown"}</b><br/>
              ${p.country || ""}<br/>
              PM2.5: <b>${p.value} µg/m³</b><br/>
              <span style="color:${AQI_COLORS[p.aqi_category] || '#ccc'}">${(p.aqi_category || "").replace(/_/g, " ")}</span>
            </div>
          `);
        }
      }).addTo(map);
    };
    window.addEventListener("mapReady", render);
    render();
    return () => window.removeEventListener("mapReady", render);
  }, [aqGeoJSON]);

  // Fire overlay
  useEffect(() => {
    const render = () => {
      const map = mapInstanceRef.current;
      const L = map?._L;
      if (!map || !L || !fires?.fires) return;
      if (map._fireLayer) map.removeLayer(map._fireLayer);
      map._fireLayer = L.layerGroup();
      fires.fires.slice(0, 200).forEach(f => {
        L.circleMarker([f.lat, f.lon], {
          radius: 4,
          fillColor: "#FF4444",
          color: "#FF6666",
          weight: 1,
          fillOpacity: 0.7,
        })
          .bindPopup(`<div style="font-family:monospace;font-size:11px;background:#0D1421;color:#E8EDF5;padding:6px;border-radius:4px">🔥 Active Fire<br/>FRP: ${f.frp} MW<br/>${f.acq_date}</div>`)
          .addTo(map._fireLayer);
      });
      map._fireLayer.addTo(map);
    };
    window.addEventListener("mapReady", render);
    render();
    return () => window.removeEventListener("mapReady", render);
  }, [fires]);

  return (
    <div className="fade-in">
      <h1 style={{ fontFamily: "var(--mono)", fontSize: 20, marginBottom: 6 }}>GIS MAP</h1>
      <p style={{ color: "var(--subtext)", fontSize: 12, marginBottom: 20 }}>
        Interactive global overlay · Air quality stations + active fire hotspots
      </p>

      {/* Legend */}
      <div style={{ display: "flex", gap: 20, marginBottom: 16, flexWrap: "wrap" }}>
        {Object.entries(AQI_COLORS).map(([k, c]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--subtext)" }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: c, display: "inline-block" }} />
            {k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--subtext)" }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#FF4444", border: "1px solid #FF6666", display: "inline-block" }} />
          Active Fire
        </div>
      </div>

      <div
        ref={mapRef}
        style={{
          width: "100%",
          height: "calc(100vh - 260px)",
          borderRadius: 12,
          border: "1px solid var(--border)",
          overflow: "hidden",
          background: "#070B14",
        }}
      />
    </div>
  );
}
